import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { listFinishVariantDeficits } from "@/lib/manufacturing/materialUsesClient";
import type { MfgFinishVariantDeficit } from "@/lib/manufacturing/types";

type UseBucket = {
  useId: string;
  label: string;
  count: number;
};

function groupByUse(deficits: MfgFinishVariantDeficit[]): UseBucket[] {
  const map = new Map<string, UseBucket>();
  for (const d of deficits) {
    const prev = map.get(d.use_id);
    if (prev) {
      prev.count += 1;
      continue;
    }
    map.set(d.use_id, {
      useId: d.use_id,
      label: d.use_name?.trim() || d.use_code || "Part use",
      count: 1,
    });
  }
  return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
}

/** Compact control beside Models workspace tabs; amber only when open deficits exist. */
export function ManufacturingVariantDeficitsControl({
  enabled,
  refreshTick,
  onReviewUse,
}: {
  enabled: boolean;
  refreshTick: number;
  onReviewUse: (useId: string) => void;
}) {
  const [deficits, setDeficits] = useState<MfgFinishVariantDeficit[]>([]);
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const reload = useCallback(async () => {
    if (!enabled) {
      setDeficits([]);
      return;
    }
    setBusy(true);
    const r = await listFinishVariantDeficits({});
    setBusy(false);
    if (!r.ok) {
      setDeficits([]);
      return;
    }
    setDeficits(r.data.deficits);
  }, [enabled]);

  useEffect(() => {
    void reload();
  }, [reload, refreshTick]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const buckets = useMemo(() => groupByUse(deficits), [deficits]);
  const total = deficits.length;
  const hasOpen = total > 0;

  function handleClick() {
    if (!hasOpen) {
      setMenuOpen((o) => !o);
      return;
    }
    if (buckets.length === 1) {
      setMenuOpen(false);
      onReviewUse(buckets[0].useId);
      return;
    }
    setMenuOpen((o) => !o);
  }

  if (!enabled) return null;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={handleClick}
        disabled={busy && !hasOpen}
        className={
          "shrink-0 whitespace-nowrap rounded-md px-3 py-1 text-sm font-semibold transition " +
          (hasOpen
            ? "bg-amber-100 text-amber-950 shadow-[0_0_0_1px_rgba(245,158,11,0.55),0_0_14px_rgba(245,158,11,0.45)] dark:bg-amber-950/60 dark:text-amber-100"
            : "bg-gray-100 text-gray-600 hover:text-gray-900 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200")
        }
        aria-haspopup={buckets.length > 1 || !hasOpen ? "menu" : undefined}
        aria-expanded={menuOpen}
        title={hasOpen ? `${total} open variant deficit${total === 1 ? "" : "s"}` : "No open deficits"}
      >
        Variant deficits{hasOpen ? ` (${total})` : ""}
      </button>

      {menuOpen ? (
        <div
          role="menu"
          className="absolute left-0 top-full z-30 mt-1 min-w-[14rem] rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
        >
          {!hasOpen ? (
            <div className="px-3 py-2 text-xs text-gray-600 dark:text-zinc-400">No open deficits.</div>
          ) : (
            buckets.map((b) => (
              <button
                key={b.useId}
                type="button"
                role="menuitem"
                className="flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-sm text-gray-900 hover:bg-amber-50 dark:text-zinc-100 dark:hover:bg-amber-950/40"
                onClick={() => {
                  setMenuOpen(false);
                  onReviewUse(b.useId);
                }}
              >
                <span className="truncate font-medium">{b.label}</span>
                <span className="shrink-0 text-xs text-amber-800 dark:text-amber-200">{b.count}</span>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
